import 'datocms-react-ui/styles.css';
import './styles/globals.css';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { connect } from 'datocms-plugin-sdk';
import { withLDProvider } from 'launchdarkly-react-client-sdk';

import ConfigScreen from './entrypoints/ConfigScreen';
import ControlGenerator from './entrypoints/ControlGenerator/ControlGenerator';
import EditorEntry from './entrypoints/EditorEntry';
import FinalVideo from './entrypoints/FinalVideo';
import RestartTranscript from './entrypoints/RestartTranscript';
import SortSource from './entrypoints/SortSource';
import UpdateClipSource from './entrypoints/UpdateClipSource';
import { withClerkProvider } from './entrypoints/withClerkProvider';
import { StoreProvider } from './stores-v2/StoreContext';
import { VideoCreatorStoreProvider } from './stores-v2/VideoCreatorStoreContext';
import { render } from './utils/render';

import type { RenderFieldExtensionCtx } from 'datocms-plugin-sdk';

const rot13 = (str: string) =>
  str.replace(/[a-zA-Z]/g, c => {
    const base = c <= 'Z' ? 65 : 97;
    return String.fromCharCode(((c.charCodeAt(0) - base + 13) % 26) + base);
  });

const ddProxy =
  process.env.REACT_APP_DD_PROXY_ENABLED === 'true'
    ? (options: { path: string; parameters: string }) =>
        `/bfwd/${encodeURIComponent(rot13(options.path))}/${encodeURIComponent(rot13(options.parameters))}`
    : undefined;

// Vercel preview origins (*.vercel.app) are not in arbor-v1's CORS allowlist,
// so any custom request header (incl. x-datadog-*) trips the preflight.
// Skip tracing-header injection on those origins to keep API calls as simple
// CORS requests. Production / dev.arborplatform.com retain full tracing.
const isCorsRestrictedHost = window.location.hostname.endsWith('.vercel.app');

datadogRum.init({
  applicationId: process.env.REACT_APP_DD_APPLICATION_ID || '',
  clientToken: process.env.REACT_APP_DD_CLIENT_TOKEN || '',
  // `site` refers to the Datadog site parameter of your organization
  // see https://docs.datadoghq.com/getting_started/site/
  site: 'datadoghq.com',
  service: 'arbor-video',
  env:
    process.env.DATO_ENV ||
    process.env.REACT_APP_DATO_ENV ||
    process.env.NODE_ENV ||
    'dev',
  // Specify a version number to identify the deployed version of your application in Datadog
  version:
    process.env.REACT_APP_PACKAGE_VERSION ??
    process.env.REACT_APP_BUILD_COMMIT_SHA ??
    undefined,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'allow',
  trackViewsManually: false,
  trackSessionAcrossSubdomains: true,
  allowedTracingUrls: isCorsRestrictedHost
    ? []
    : [process.env.REACT_APP_API_URL || ''],
  ...(ddProxy && { proxy: ddProxy }),
});

datadogLogs.init({
  clientToken: process.env.REACT_APP_DD_CLIENT_TOKEN || '',
  site: 'datadoghq.com',
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
  forwardConsoleLogs: 'all',
  service: 'arbor-video',
  version:
    process.env.REACT_APP_PACKAGE_VERSION ??
    process.env.REACT_APP_BUILD_COMMIT_SHA ??
    undefined,
  env:
    process.env.DATO_ENV ||
    process.env.REACT_APP_DATO_ENV ||
    process.env.NODE_ENV ||
    'dev',
  trackSessionAcrossSubdomains: true,
  ...(ddProxy && { proxy: ddProxy }),
});

const isInIframe = window.location !== window.parent.location;

if (isInIframe) {
  connect({
    renderConfigScreen(ctx) {
      return render(<ConfigScreen ctx={ctx} />);
    },
    manualFieldExtensions() {
      return [
        {
          id: 'videoEditor',
          name: 'Video Editor',
          type: 'addon',
          fieldTypes: ['link'],
        },
        {
          id: 'updateClipSource',
          name: 'Update Clip Source',
          type: 'addon',
          fieldTypes: ['file'],
        },
        {
          id: 'restartTranscriptGeneration',
          name: 'Restart Transcript Generation',
          type: 'addon',
          fieldTypes: ['link'],
        },
        {
          id: 'generateControls',
          name: 'Generate Controls',
          type: 'addon',
          fieldTypes: ['json'],
        },
        {
          id: 'sortSource',
          name: 'Sort Source',
          type: 'addon',
          fieldTypes: ['json'],
        },
      ];
    },
    renderFieldExtension(
      fieldExtensionId: string,
      ctx: RenderFieldExtensionCtx,
    ) {
      switch (fieldExtensionId) {
        case 'videoEditor':
          return render(
            <>
              <FinalVideo ctx={ctx} />
            </>,
          );
        case 'updateClipSource':
          return render(
            <>
              <UpdateClipSource ctx={ctx} />
            </>,
          );
        case 'restartTranscriptGeneration':
          return render(
            <>
              <RestartTranscript ctx={ctx} />
            </>,
          );
        case 'generateControls':
          return render(
            <>
              <ControlGenerator ctx={ctx} />
            </>,
          );
        case 'sortSource':
          return render(
            <>
              <SortSource ctx={ctx} />
            </>,
          );
      }
    },
  });
} else {
  // Context should be deeper but this is a quick fix (this will stay here forever)
  // Dependency issue: EditorEntry needs Dato context, which is currently in VideoCreator...

  // Build LaunchDarkly options
  const ldOptions: Record<string, string | boolean> = {
    useReport: true,
    streaming: false,
  };
  const streamUrl = process.env.REACT_APP_LAUNCH_DARKLY_STREAM_URL;
  if (typeof streamUrl === 'string' && streamUrl) {
    ldOptions.streamUrl = streamUrl;
  }
  const baseUrl = process.env.REACT_APP_LAUNCH_DARKLY_BASE_URL;
  if (typeof baseUrl === 'string' && baseUrl) {
    ldOptions.baseUrl = baseUrl;
  }
  const eventsUrl = process.env.REACT_APP_LAUNCH_DARKLY_EVENTS_URL;
  if (typeof eventsUrl === 'string' && eventsUrl) {
    ldOptions.eventsUrl = eventsUrl;
  }

  const ldClientSideIdEnv = process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID;
  const ldClientSideID =
    typeof ldClientSideIdEnv === 'string' ? ldClientSideIdEnv : '';

  const AppWithProviders = withClerkProvider(
    withLDProvider({
      clientSideID: ldClientSideID,
      options: ldOptions,
    })(() => (
      <StoreProvider>
        <VideoCreatorStoreProvider>
          <EditorEntry />
        </VideoCreatorStoreProvider>
      </StoreProvider>
    )),
  );

  render(<AppWithProviders />);
}
